import React, { useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Upload, 
  X, 
  CheckCircle2,
  Loader2,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    postcode: '',
    work_type: '',
    start_date: '',
    message: '',
    consent: false,
    marketing: false,
  });
  const [errors, setErrors] = useState({});

  const workTypes = [
    { value: 'skim', label: 'Skimming (walls/ceilings)' },
    { value: 'ceiling', label: 'Ceiling only' },
    { value: 'patch', label: 'Patch repairs' },
    { value: 'coving', label: 'Cornice & Coving' },
    { value: 'render', label: 'Rendering (external)' },
    { value: 'not_sure', label: 'Not sure / Other' },
  ];

  const startDates = [
    { value: 'asap', label: 'ASAP' },
    { value: '2_4_weeks', label: '2-4 weeks' },
    { value: '1_2_months', label: '1-2 months' },
    { value: 'flexible', label: 'Flexible / Planning ahead' },
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.work_type) newErrors.work_type = 'Please select the type of work';
    if (!formData.start_date) newErrors.start_date = 'Please select a timeframe';
    if (!formData.consent) newErrors.consent = 'Please confirm you agree to data handling';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024;
      if (!isValid) {
        toast.error(`${file.name} is not valid. Please upload images under 10MB.`);
      }
      return isValid;
    });

    if (uploadedFiles.length + validFiles.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }

    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('postcode', formData.postcode);
      formDataToSend.append('work_type', formData.work_type);
      formDataToSend.append('start_date', formData.start_date);
      formDataToSend.append('message', formData.message || '');
      formDataToSend.append('consent', formData.consent);

      uploadedFiles.forEach((file) => {
        formDataToSend.append('photos', file);
      });

      const response = await axios.post(`${BACKEND_URL}/api/quote`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setIsSuccess(true);
        toast.success('Quote request submitted successfully!');
      } else {
        toast.error(response.data.message || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="contact" className="py-20 lg:py-28 bg-white" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-white">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 font-['Manrope'] mb-4">
                Thank You!
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Your quote request has been received. We'll review your details and 
                get back to you within 24 hours.
              </p>
              <div className="flex items-center justify-center gap-2 text-slate-500 mb-8">
                <Clock className="w-5 h-5" />
                <span>Typical response time: same day</span>
              </div>
              <p className="text-slate-600">
                Need a faster response?{' '}
                <a href="tel:07766111127" className="text-red-600 font-semibold hover:underline">
                  Call us on 07766 111127
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="contact" 
      className="py-20 lg:py-28 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-red-600 font-semibold text-sm tracking-wider uppercase mb-4">
            Get in Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Manrope'] mb-6">
            Request Your Free Quote
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Tell us about your project and we'll get back to you with a quote or contact you to arrange a survey. 
            Photos help us give you the most accurate estimate.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Phone */}
              <Card className="border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 font-['Manrope'] mb-1">
                        Call Us
                      </h3>
                      <a 
                        href="tel:07766111127" 
                        className="text-xl font-bold text-red-600 hover:text-red-700"
                        data-testid="contact-phone"
                      >
                        07766 111127
                      </a>
                      <p className="text-sm text-slate-500 mt-1">
                        Mon-Fri 8am-6pm, Sat 9am-2pm
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 font-['Manrope'] mb-1">
                        Email Us
                      </h3>
                      <a 
                        href="mailto:info@flexplastering.co.uk" 
                        className="text-red-600 hover:text-red-700 font-medium break-all"
                        data-testid="contact-email"
                      >
                        info@flexplastering.co.uk
                      </a>
                      <p className="text-sm text-slate-500 mt-1">
                        We aim to respond within 24 hours
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address */}
              <Card className="border border-slate-100 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 font-['Manrope'] mb-1">
                        Based in
                      </h3>
                      <p className="text-slate-600">
                        Northampton
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        Covering 30-mile radius
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Quote Form */}
          <div className="lg:col-span-2">
            <Card className="border border-slate-200 shadow-lg">
              <CardContent className="p-8 lg:p-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 font-medium">
                        Name <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className={`bg-slate-50 border-slate-200 focus:ring-red-500 focus:border-red-500 ${
                          errors.name ? 'border-red-500' : ''
                        }`}
                        data-testid="input-name"
                      />
                      {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-medium">
                        Phone <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="07xxx xxxxxx"
                        className={`bg-slate-50 border-slate-200 focus:ring-red-500 focus:border-red-500 ${
                          errors.phone ? 'border-red-500' : ''
                        }`}
                        data-testid="input-phone"
                      />
                      {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-medium">
                        Email <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                        className={`bg-slate-50 border-slate-200 focus:ring-red-500 focus:border-red-500 ${
                          errors.email ? 'border-red-500' : ''
                        }`}
                        data-testid="input-email"
                      />
                      {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                    </div>

                    {/* Postcode */}
                    <div className="space-y-2">
                      <Label htmlFor="postcode" className="text-slate-700 font-medium">
                        Postcode <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="postcode"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        placeholder="NN1 1AA"
                        className={`bg-slate-50 border-slate-200 focus:ring-red-500 focus:border-red-500 ${
                          errors.postcode ? 'border-red-500' : ''
                        }`}
                        data-testid="input-postcode"
                      />
                      {errors.postcode && <p className="text-red-500 text-sm">{errors.postcode}</p>}
                    </div>

                    {/* Work Type */}
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Type of Work <span className="text-red-600">*</span>
                      </Label>
                      <Select 
                        value={formData.work_type} 
                        onValueChange={(value) => handleSelectChange('work_type', value)}
                      >
                        <SelectTrigger 
                          className={`bg-slate-50 border-slate-200 ${errors.work_type ? 'border-red-500' : ''}`}
                          data-testid="select-work-type"
                        >
                          <SelectValue placeholder="Select type of work" />
                        </SelectTrigger>
                        <SelectContent>
                          {workTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.work_type && <p className="text-red-500 text-sm">{errors.work_type}</p>}
                    </div>

                    {/* Start Date */}
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        When do you need this done? <span className="text-red-600">*</span>
                      </Label>
                      <Select 
                        value={formData.start_date} 
                        onValueChange={(value) => handleSelectChange('start_date', value)}
                      >
                        <SelectTrigger 
                          className={`bg-slate-50 border-slate-200 ${errors.start_date ? 'border-red-500' : ''}`}
                          data-testid="select-start-date"
                        >
                          <SelectValue placeholder="Select timeframe" />
                        </SelectTrigger>
                        <SelectContent>
                          {startDates.map((date) => (
                            <SelectItem key={date.value} value={date.value}>
                              {date.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.start_date && <p className="text-red-500 text-sm">{errors.start_date}</p>}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Tell us about your project
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Describe the work you need - room size, current condition, any special requirements..."
                      rows={4}
                      className="bg-slate-50 border-slate-200 focus:ring-red-500 focus:border-red-500"
                      data-testid="input-message"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">
                      Upload Photos (optional)
                    </Label>
                    <div 
                      className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-orange-300 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        data-testid="input-photos"
                      />
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">
                        Click to upload or drag photos here
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        Max 5 photos, 10MB each. JPG, PNG supported.
                      </p>
                    </div>
                    
                    {/* Uploaded Files Preview */}
                    {uploadedFiles.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-4">
                        {uploadedFiles.map((file, index) => (
                          <div 
                            key={index}
                            className="relative group bg-slate-100 rounded-lg p-2 flex items-center gap-2"
                          >
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt={file.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                            <span className="text-sm text-slate-600 max-w-[100px] truncate">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Remove file"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Consent */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({ ...prev, consent: checked }));
                        if (errors.consent) setErrors(prev => ({ ...prev, consent: '' }));
                      }}
                      className="mt-1 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                      data-testid="checkbox-consent"
                    />
                    <div>
                      <Label 
                        htmlFor="consent" 
                        className={`text-sm text-slate-600 cursor-pointer ${errors.consent ? 'text-red-500' : ''}`}
                      >
                        I agree to Flex Plastering storing my details to respond to this enquiry. <span className="text-red-600">*</span>
                      </Label>
                      {errors.consent && <p className="text-red-500 text-sm mt-1">{errors.consent}</p>}
                    </div>
                  </div>

                  {/* Marketing Consent */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="marketing"
                      checked={formData.marketing}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({ ...prev, marketing: checked }));
                      }}
                      className="mt-1 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                      data-testid="checkbox-marketing"
                    />
                    <div>
                      <Label 
                        htmlFor="marketing" 
                        className="text-sm text-slate-600 cursor-pointer"
                      >
                        I would like to receive updates, promotions, and news from Flex Plastering.
                      </Label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                    disabled={isSubmitting}
                    data-testid="submit-quote-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Send Quote Request'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
